import React, { useState } from "react";
import Loader from "../../../common/loader";
import { formatPostedOnDate, extractCandidateName } from "../../../common/helpers";
import { RecruiterApi } from "../../../supports/recruiterApi";
import { useToast } from '../../../common/toastSystem';


const JobApplicantsTable = ({ applicants, loading, error }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [cvUrl, setCvUrl] = useState("");
  const [loadingCV, setLoadingCV] = useState(false);
  const [fileType, setFileType] = useState("");

  const toast = useToast();

  const getFileExtension = (url) => {
    const urlWithoutParams = url.split('?')[0];
    const extension = urlWithoutParams.split('.').pop().toLowerCase();
    return extension;
  };

  const getViewerUrl = (url, extension) => {
    // PDF files can be viewed directly
    if (extension === 'pdf') {
      return url;
    }
    
    // DOCX and DOC files need a viewer
    if (extension === 'docx' || extension === 'doc') {
      // Option 1: Google Docs Viewer
      return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
      
      // Option 2: Microsoft Office Viewer (uncomment to use this instead)
      // return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    }
    
    // For other file types, try direct view
    return url;
  };

  const handleViewCV = async (app) => {
    try {
      setLoadingCV(true);
      setModalOpen(true);
      
      const params = {
        file_url: app.mph_candidateoriginalcv,
        user_payload: {
          tenant: "SYSTEM",
          email: "mohammed.ashique@gapblue.com",
          request_id: "Req_1234",
          candidate_id: app.mph_jobapplicantid,
        },
      };

      const res = await RecruiterApi.viewApplicantCV(params);

      const sasUrl =
        res?.data?.data?.result?.result?.sas_token_url ||
        res?.data?.result?.result?.sas_token_url;

      if (sasUrl) {
        const extension = getFileExtension(app.mph_candidateoriginalcv);
        setFileType(extension);
        const viewerUrl = getViewerUrl(sasUrl, extension);
        setCvUrl(viewerUrl);
      } else {
        console.error("SAS URL not found in response", res);
        toast.warning("Requested CV not found in CRM");
        // alert("Requested CV not found in CRM.");
        setModalOpen(false);
      }
    } catch (error) {
      console.error("Error fetching SAS URL:", error);
      // alert("Failed to open CV.");
      toast.warning("Failed to open CV");
      setModalOpen(false);
    } finally {
      setLoadingCV(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setCvUrl("");
    setFileType("");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader message="Loading applicants..." />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-xs p-4">{error}</div>;
  }

  if (!applicants || applicants.length === 0) {
    return (
      <div className="text-gray-500 text-sm p-4">
        No applicants found for this job.
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto w-full">
        <table className="min-w-full divide-y divide-gray-200 border table-fixed">
          <colgroup>
            <col style={{ width: "20%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "20%" }} />
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-666  tracking-wider">
                Candidate
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-666  tracking-wider">
                Status
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-666  tracking-wider">
                Original CV
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-666  tracking-wider">
                Submitted For Approval On
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-666  tracking-wider">
                Rate
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-666  tracking-wider">
                Notice Period
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-666  tracking-wider">
                Recruiter
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100 text-xs text-gray-333">
            {applicants.map((app, idx) => {
              const sourcerName =
                app["_mph_sourcerid_value@OData.Community.Display.V1.FormattedValue"] ||
                "-";

              return (
                <tr key={app.mph_jobapplicantid || idx}>
                  <td className="px-4 py-2 whitespace-nowrap overflow-hidden text-ellipsis max-w-[220px]">
                    <div
                      className="text-xs text-gray-333 truncate"
                      title={
                        extractCandidateName
                          ? extractCandidateName(app.mph_name)
                          : app.mph_name
                      }
                    >
                      {extractCandidateName
                        ? extractCandidateName(app.mph_name)
                        : app.mph_name}
                    </div>
                  </td>

                  <td className="px-4 py-2 whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px]">
                    <span className="truncate block" title={app.status_name || "-"}>
                      {app.status_name || "-"}
                    </span>
                  </td>

                  <td className="px-4 py-2 whitespace-nowrap overflow-hidden text-ellipsis max-w-[160px]">
                    {app.mph_candidateoriginalcv ? (
                      <button
                        type="button"
                        onClick={() => handleViewCV(app)}
                        className="text-gray-333 underline text-xs truncate block cursor-pointer bg-transparent border-0 p-0"
                        title={app.mph_candidateoriginalcv}
                      >
                        View CV
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs" title="Not Available">
                        Not Available
                      </span>
                    )}
                  </td>

                  <td className="px-4 py-2 whitespace-nowrap overflow-hidden text-ellipsis max-w-[220px]">
                    <span
                      className="truncate block"
                      title={
                        app.mph_submittedforapprovalon
                          ? formatPostedOnDate(app.mph_submittedforapprovalon)
                          : "-"
                      }
                    >
                      {app.mph_submittedforapprovalon
                        ? formatPostedOnDate(app.mph_submittedforapprovalon)
                        : "-"}
                    </span>
                  </td>

                  <td className="px-4 py-2 whitespace-nowrap overflow-hidden text-ellipsis max-w-[220px]">
                    <span className="truncate block" title={app.mph_expectedrate || "-"}>
                      {app.mph_expectedrate || "-"}
                    </span>
                  </td>

                  <td className="px-4 py-2 whitespace-nowrap overflow-hidden text-ellipsis max-w-[220px]">
                    <span className="truncate block" title={app.mph_noticeperiod || "-"}>
                      {app.mph_noticeperiod || "-"}
                    </span>
                  </td>

                  {/* ➤ SOURCER NAME COLUMN */}
                  <td className="px-4 py-2 whitespace-nowrap overflow-hidden text-ellipsis max-w-[220px]">
                    <span className="truncate block" title={sourcerName}>
                      {sourcerName}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>

        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 h-5/6 flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-800">
                Resume
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none"
              >
                &times;
              </button>
            </div>

            <div className="flex-1 p-4 overflow-hidden">
              {loadingCV ? (
                <div className="flex justify-center items-center h-full">
                  <Loader message="Loading CV..." />
                </div>
              ) : (
                <iframe
                  src={cvUrl}
                  className="w-full h-full border-0"
                  title="Candidate CV"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default JobApplicantsTable;